import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { config, describeDataOrigin, type ApiSystem } from '@/config/env';
import { buildInfo, shortSha } from '@/config/buildInfo';
import { useServices } from '@/lib/ServicesContext';
import { useAuth } from '@/lib/auth/AuthContext';
import { describeApiError } from '@/lib/api/errors';

interface Probe {
  system: ApiSystem;
  label: string;
  path: string;
}

// Sonda READ-ONLY — sonda, która zapisuje cokolwiek, nie jest sondą.
//
// JEDNA, bo jest jedno wejście: przeglądarka rozmawia wyłącznie z backendem Control Center,
// a stan poszczególnych źródeł ten backend raportuje w treści odpowiedzi. Trzy osobne sondy
// opisywały poprzednią architekturę i pytały o `/health` na korzeniu — czyli trzy razy 404.
const PROBES: Probe[] = [
  { system: 'crm', label: 'Backend Control Center', path: '/api/cc/health' },
];

function useProbe(probe: Probe) {
  const { api } = useServices();
  return useQuery({
    queryKey: ['probe', probe.system],
    queryFn: async () => {
      const started = performance.now();
      await api.get<unknown>(probe.system, probe.path);
      return Math.round(performance.now() - started);
    },
    retry: false,
    staleTime: 15_000,
  });
}

function ProbeRow({ probe }: { probe: Probe }) {
  const { api } = useServices();
  const { data, error, isLoading } = useProbe(probe);
  return (
    <TableRow>
      <TableCell>{probe.label}</TableCell>
      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
        {`${api.baseFor(probe.system)}${probe.path}`}
      </TableCell>
      <TableCell>
        {isLoading ? (
          <Chip size="small" label="sprawdzam…" />
        ) : error ? (
          <Chip size="small" color="error" variant="outlined" label="niedostępne" />
        ) : (
          <Chip size="small" color="success" variant="outlined" label={`OK · ${data} ms`} />
        )}
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {error ? describeApiError(error) : '—'}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/**
 * Stan łączników do źródeł — CRM i Drive.
 *
 * ┌─ CO ZASTĘPUJE ──────────────────────────────────────────────────────────────────────────────┐
 * │ Kiedy źródło miało problem, konsola pokazywała FORMULARZ POŚWIADCZEŃ tego źródła — jakby    │
 * │ rozwiązaniem było zalogowanie się do niego ponownie. To już nieprawda dwa razy: do źródeł   │
 * │ chodzi backend własną tożsamością, a użytkownik nie ma i nie powinien mieć tych haseł.      │
 * │ Formularz w tej sytuacji prosił więc o coś, czego podanie niczego by nie naprawiło.         │
 * │                                                                                              │
 * │ Właściwą odpowiedzią jest STAN: które źródło działa, które nie, i od kiedy o tym wiadomo.   │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Backend nie wypuszcza tu adresów ani nazw kont bazy (nazwa konta to połowa poświadczenia),
 * więc ekran pokazuje dokładnie tyle, ile wie: nazwę źródła i jego stan.
 */
const OPIS_STANU: Record<string, { etykieta: string; kolor: 'success' | 'warning' | 'error' | 'default'; co: string }> = {
  SPRAWNY: { etykieta: 'POŁĄCZONE', kolor: 'success', co: 'Połączenie działa, a dowód trybu tylko do odczytu przeszedł.' },
  // UWAGA NA TREŚĆ TEGO ZDANIA. Pierwsza wersja mówiła „dane nie są wydawane" — i była
  // NIEPRAWDZIWA: stan łącznika jest raportem, a nie zaworem; trasy danych chodzą do puli
  // bezpośrednio. Fail-closed siedzi gdzie indziej — `index.js` odmawia STARTU w trybie
  // PROD_READONLY, gdy choć jedno wymagane źródło nie potwierdziło braku prawa zapisu.
  // Fałszywe zdanie o bezpieczeństwie jest gorsze niż jego brak, bo bywa czytane jak gwarancja.
  NIEZAUFANY: {
    etykieta: 'NIEPOTWIERDZONE',
    kolor: 'warning',
    co: 'Połączenie jest, ale konto nie udowodniło, że nie może pisać. W trybie produkcyjnym taki '
      + 'stan nie pozwala usłudze wstać — jeżeli go widzisz, ta usługa nie pracuje na produkcji.',
  },
  NIEDOSTEPNY: { etykieta: 'NIEDOSTĘPNE', kolor: 'error', co: 'Nie udało się połączyć ze źródłem.' },
  NIESKONFIGUROWANY: { etykieta: 'NIESKONFIGUROWANE', kolor: 'default', co: 'Nie podano adresu. To nie jest awaria — Control Center nie odpytuje tego źródła.' },
};

const NAZWA_ZRODLA: Record<string, string> = { crm: 'CRM', drive: 'Drive (kierowcy)', main: 'System główny' };

interface OdpowiedzZdrowia {
  status: string;
  laczniki: { zrodlo: string; stan: string }[];
}

/** Czy to, co przyszło, ma kształt odpowiedzi zdrowia — sprawdzane, bo idzie z ciała błędu. */
function czyOdpowiedzZdrowia(x: unknown): x is OdpowiedzZdrowia {
  return typeof x === 'object' && x !== null && Array.isArray((x as OdpowiedzZdrowia).laczniki);
}

function StanZrodel() {
  const { api } = useServices();
  const { data, error, isLoading } = useQuery({
    queryKey: ['stan-zrodel'],
    /*
     * `/api/cc/health` odpowiada 503, GDY KTÓREŚ WYMAGANE ŹRÓDŁO NIE DZIAŁA — czyli dokładnie
     * wtedy, kiedy ten ekran jest najbardziej potrzebny. Klient API traktuje 503 jak błąd
     * i rzuca, więc naiwne `api.get` zamieniało odpowiedź niosącą komplet stanów w komunikat
     * „backend nie odpowiada". Backend odpowiadał — po prostu nie dwusetką.
     *
     * `ApiError` niesie `body`, więc treść da się odzyskać. Odzyskujemy ją TYLKO wtedy, gdy ma
     * kształt odpowiedzi zdrowia; inaczej błąd leci dalej jako błąd, bo cisza i awaria to dwa
     * różne zdania i żadne nie może udawać drugiego.
     */
    queryFn: async () => {
      try {
        return await api.get<OdpowiedzZdrowia>('crm', '/api/cc/health');
      } catch (err) {
        const cialo = (err as { body?: unknown })?.body;
        if (czyOdpowiedzZdrowia(cialo)) return cialo;
        throw err;
      }
    },
    retry: false,
    staleTime: 15_000,
  });

  const laczniki = data?.laczniki ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Źródła danych
      </Typography>

      {isLoading ? <Chip size="small" label="sprawdzam…" /> : null}

      {/* Gdy backend w ogóle nie odpowiada, NIE UDAJEMY, że znamy stan źródeł. „Nie wiadomo"
          i „nie działa" to dwa różne zdania i mylenie ich kończy się szukaniem awarii nie tam. */}
      {error ? (
        <Typography variant="body2" color="text.secondary">
          Backend Control Center nie odpowiada, więc stan źródeł jest nieznany — {describeApiError(error)}
        </Typography>
      ) : null}

      {!isLoading && !error && laczniki.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Odpowiedź nie zawiera listy łączników.
        </Typography>
      ) : null}

      <Stack spacing={1}>
        {laczniki.map((l) => {
          const opis = OPIS_STANU[l.stan] ?? {
            etykieta: l.stan,
            kolor: 'default' as const,
            co: 'Stan nieznany tej wersji interfejsu.',
          };
          return (
            <Stack key={l.zrodlo} direction="row" spacing={1.5} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ minWidth: 160 }}>
                {NAZWA_ZRODLA[l.zrodlo] ?? l.zrodlo}
              </Typography>
              <Chip size="small" variant="outlined" color={opis.kolor} label={opis.etykieta} />
              <Typography variant="caption" color="text.secondary">
                {opis.co}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}

/**
 * Status połączeń. Jedyny ekran, który wolno pokazać bez sesji — mówi wyłącznie o konfiguracji
 * i o tym, czy backend odpowiada. Nie ujawnia żadnych danych domenowych.
 */
export function SystemStatusPage() {
  const auth = useAuth();

  return (
    <Box>
      <NaglowekEkranu
        tytul="Status połączeń"
        opis="Czy przeglądarka rozmawia z backendem Control Center, w jakim środowisku i z jakiego artefaktu zbudowano tę aplikację."
      />

      <StanZrodel />

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
              Środowisko
            </Typography>
            <Typography variant="body2">{config.environment}</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
              Pochodzenie danych
            </Typography>
            <Typography variant="body2">{describeDataOrigin(config.dataOrigin)}</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
              Tryb zapisu
            </Typography>
            <Typography variant="body2">
              {config.readOnly ? 'tylko odczyt (zapis zablokowany w kliencie)' : 'zapis dozwolony'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
              Sesja
            </Typography>
            <Typography variant="body2">
              {auth.providers
                .map((p) => {
                  const id = auth.realms[p.realm]?.identity;
                  return `${p.label}: ${id ? `${id.subject} (${id.roles.join(', ')})` : 'nie podłączono'}`;
                })
                .join(' · ')}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
              Uprawnienia
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              {auth.permissions.size ? [...auth.permissions].join(', ') : '—'}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/*
        Tożsamość artefaktu. Bez tego jedynym sposobem ustalenia „co tu właściwie stoi” była
        reprodukcja builda i porównanie sum kontrolnych. `data-testid` jest tu po to, żeby test
        E2E potrafił odczytać SHA i porównać go ze źródłem — weryfikacja wdrożenia nie powinna
        polegać na czytaniu ekranu przez człowieka.
      */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }} data-testid="build-info">
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Wdrożony artefakt
        </Typography>
        <Stack spacing={1}>
          {(
            [
              ['Wersja aplikacji', buildInfo.appVersion, undefined],
              ['Commit', shortSha(buildInfo.gitSha), 'build-info-sha'],
              ['Gałąź', buildInfo.gitBranch, undefined],
              ['Zbudowano', buildInfo.buildTime, undefined],
              ['Środowisko builda', buildInfo.environment, undefined],
            ] as const
          ).map(([etykieta, wartosc, testid]) => (
            <Stack key={etykieta} direction="row" spacing={1}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
                {etykieta}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }} data-testid={testid}>
                {wartosc}
              </Typography>
            </Stack>
          ))}
          {buildInfo.gitDirty && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Build z niezacommitowanego drzewa — artefaktu nie da się jednoznacznie powiązać z commitem"
              sx={{ alignSelf: 'flex-start', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
          )}
        </Stack>
      </Paper>

      <Table size="small" aria-label="Sondy backendów">
        <TableHead>
          <TableRow>
            <TableCell>System</TableCell>
            <TableCell>Adres sondy</TableCell>
            <TableCell>Stan</TableCell>
            <TableCell>Uwagi</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {PROBES.map((probe) => (
            <ProbeRow key={probe.system} probe={probe} />
          ))}
        </TableBody>
      </Table>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        „Niedostępne” oznacza wyłącznie, że pod tym adresem nic nie odpowiada w tym środowisku — nie oznacza awarii
        systemu produkcyjnego.
      </Typography>
    </Box>
  );
}
